// Pure helpers behind the single "Preview as" control shared by Review.jsx
// (screen + send-test button) and Sms.jsx (phone preview): given who is
// currently selected, resolve which person's real values to show, then
// substitute {{first_name}}, {{employee_id}}, and every recipient-field
// token in a body for that person.

// Sentinel value for "Me" in the "Preview as" select — distinct from any
// real user UUID.
export const ME = '__me__';

// "Preview as" lists at most this many people at once; typing a name
// narrows the list. Rendering a whole audience as options (39k on one board)
// froze the Review step.
export const PREVIEW_AS_LIMIT = 50;

const firstNameOf = name => (name || '').trim().split(/\s+/)[0] || '';

// The people "Preview as" lists for what has been typed: names containing
// the query (case-insensitive), capped at PREVIEW_AS_LIMIT, plus how many
// matched in total so the list can say it was cut short.
export const previewAsMatches = (recipientRows = [], query = '') => {
  const needle = query.trim().toLowerCase();
  const matches = recipientRows.filter(
    row => row.name && row.name.toLowerCase().includes(needle),
  );

  return { rows: matches.slice(0, PREVIEW_AS_LIMIT), total: matches.length };
};

// Resolves the person driving previews. "Me" (the default) tries to match
// the current admin against the recipient rows by email, so their own field
// values show up when they also happen to be a recipient of this broadcast;
// otherwise their profile name is used with no employee ID or field values.
// Any other selection resolves to the matching recipient row.
export const resolvePreviewPerson = ({
  selectedUuid,
  currentUser = {},
  recipientRows = [],
}) => {
  if (!selectedUuid || selectedUuid === ME) {
    const ownRow = recipientRows.find(
      row =>
        row.email &&
        currentUser.email &&
        row.email.toLowerCase() === currentUser.email.toLowerCase(),
    );

    const name = currentUser.name || ownRow?.name || '';

    return {
      userUuid: ownRow?.userUuid || null,
      name,
      firstName: firstNameOf(name),
      employeeId: ownRow?.employeeId || null,
      isMe: true,
    };
  }

  const row = recipientRows.find(r => r.userUuid === selectedUuid);

  return {
    userUuid: selectedUuid,
    name: row?.name || '',
    firstName: firstNameOf(row?.name),
    employeeId: row?.employeeId || null,
    isMe: false,
  };
};

// Swaps per-recipient tokens for the previewed person's real values. A
// no-op when there's no text or no resolved person yet, so callers can use
// it unconditionally.
// Older drafts wrapped inserted tokens in a colored <span>; the sent email
// drops that wrapper (see BroadcastMessagePresenter#unwrap_styled_token), so
// the preview does too.
export const unwrapStyledToken = (text, token) => {
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(
    `<span\\b[^>]*>(?:\\s|&nbsp;)*(${escaped})(?:\\s|&nbsp;)*</span>`,
    'g',
  );

  return text.replace(pattern, '$1');
};

export const substituteRecipientTokens = (
  text,
  { person, recipientFields = [], t },
) => {
  if (!text || !person) return text;

  const placeholder = t ? t('reviewStep.noValuePlaceholder') : '[no value]';

  const replaceToken = (source, token, value) =>
    unwrapStyledToken(source, token).replaceAll(token, value);

  let result = replaceToken(text, '{{first_name}}', person.firstName || '');
  result = replaceToken(
    result,
    '{{employee_id}}',
    person.employeeId || placeholder,
  );

  recipientFields.forEach(field => {
    const value = person.userUuid ? field.values?.[person.userUuid] : undefined;

    result = replaceToken(result, field.token, value || placeholder);
  });

  return result;
};
